import sortBy from 'lodash/sortBy';

import type {
  Filter,
  Offer,
  Partner,
  PartnerOffers,
} from '@shared/types/frontend/ubff/hotel-detail-price';

export const DEFAULT_RATES_NUM = 4;

const hasFreeCancellation = (offer: Offer) =>
  offer.features?.some(
    (feature) =>
      feature.featureType === 'FEATURE_TYPE_CANCELLATION_POLICY' &&
      feature.value === 'free_cancellation',
  );

const hasBreakfastIncluded = (offer: Offer) =>
  offer.features?.some(
    (feature) =>
      feature.featureType === 'FEATURE_TYPE_MEAL_PLAN' &&
      feature.value === 'breakfast_included',
  );

type EnhancedOffer = {
  offer: Offer;
  partner: Partner;
  partnerRanking: number;
  offerPosition: number;
};

export const getEnhancedOffers = (
  partnersOffersList: PartnerOffers[],
): EnhancedOffer[] => {
  const top4Partners = partnersOffersList.slice(0, 4);

  const top4HeroOffers = top4Partners.map((partner) => partner.offers[0]);
  const allTop4LackFCBI = top4HeroOffers.every(
    (offer) => !hasFreeCancellation(offer) && !hasBreakfastIncluded(offer),
  );

  let insertedFC = false;
  let insertedBI = false;

  const finalResult: EnhancedOffer[] = [];

  const insertSecondaryOffers = (
    offers: Offer[],
    partner: Partner,
    partnerRanking: number,
  ) => {
    const result: EnhancedOffer[] = [];
    let count = 0;

    for (let j = 1; j < offers.length; j += 1) {
      const offer = offers[j];

      if (!insertedFC && hasFreeCancellation(offer) && count < 1) {
        result.push({
          offer,
          partner,
          partnerRanking,
          offerPosition: j + 1,
        });
        insertedFC = true;
        count += 1;
      } else if (!insertedBI && hasBreakfastIncluded(offer) && count < 1) {
        result.push({
          offer,
          partner,
          partnerRanking,
          offerPosition: j + 1,
        });
        insertedBI = true;
        count += 1;
      }

      if (insertedFC && insertedBI) break;
    }

    return result;
  };

  for (let i = 0; i < top4Partners.length; i += 1) {
    const { offers, partner } = top4Partners[i];
    const partnerRanking = i + 1;

    if (offers && offers.length > 0) {
      const resultForPartner: EnhancedOffer[] = [
        {
          offer: offers[0],
          partner,
          partnerRanking,
          offerPosition: 1,
        },
      ];

      if (allTop4LackFCBI && offers.length > 1) {
        const secondaries = insertSecondaryOffers(
          offers,
          partner,
          partnerRanking,
        );
        resultForPartner.push(...secondaries);
      }

      finalResult.push(...resultForPartner);
    }
  }

  return finalResult;
};

export const getRemainingPartnerOffers = (
  enhancedOffers: EnhancedOffer[],
  partnersOffersList: PartnerOffers[],
) => {
  const shownPartnerIds = new Set(
    enhancedOffers.map((item) => item.partner.id),
  );
  return partnersOffersList
    .filter((partner) => !shownPartnerIds.has(partner.partner.id))
    .map((partner) => ({
      offer: partner.offers[0],
      partner: partner.partner,
      partnerRanking:
        partnersOffersList.findIndex(
          (pp) => pp.partner.id === partner.partner.id,
        ) + 1,
      offerPosition: 1,
    }));
};

export const getEnhancedPartnersOffersList = ({
  enableMultiplePriceOnDetails,
  enhancedOffers,
  isExpanded,
  partnersOffersList,
  priceFilters,
  restPartnersOffersList,
}: {
  partnersOffersList: PartnerOffers[];
  isExpanded: boolean;
  enableMultiplePriceOnDetails: boolean;
  priceFilters?: Filter[];
  restPartnersOffersList: EnhancedOffer[];
  enhancedOffers: EnhancedOffer[];
}) => {
  const fallbackOffers = partnersOffersList.map((partner, index) => ({
    offer: partner.offers[0],
    partner: partner.partner,
    partnerRanking: index + 1,
    offerPosition: 1,
  }));

  if (
    !enableMultiplePriceOnDetails ||
    (priceFilters && priceFilters?.length > 0)
  ) {
    return isExpanded
      ? fallbackOffers
      : fallbackOffers.slice(0, DEFAULT_RATES_NUM);
  }

  return isExpanded
    ? [...enhancedOffers, ...restPartnersOffersList]
    : enhancedOffers;
};

export const getLowestPriceInRestOffers = (
  offers: EnhancedOffer[],
): number | undefined => {
  if (offers.length === 0) return undefined;

  const sorted = sortBy(offers, (offer) => offer.offer.price.price);

  return sorted[0].offer.price.price;
};
